import clsx from 'clsx';
import { Globe, Hash, Lock } from 'lucide-react';

import { CLIENT_CHANNELS, type Channel } from '../config/channels';
import { useShell } from './state';
import styles from './shell.module.css';

/**
 * Channel list — the Discord-style second column.
 *
 * Phase 1 channels are hardcoded (Q-C10). Rendered with an icon appropriate
 * to their kind: `Hash` for text channels, `Lock` overlay for read-only
 * placeholders (`#general` / `#random`), `Globe` for the world channel.
 */
export function ChannelList(): JSX.Element {
  const activeChannel = useShell((s) => s.activeChannel);
  const unread = useShell((s) => s.unread);
  const selectChannel = useShell((s) => s.selectChannel);

  return (
    <section className={styles.channelList} aria-label="Channels">
      <header className={styles.channelListHeader}>MyTile</header>
      <div className={styles.channelListScroll}>
        <div className={styles.channelSectionTitle}>Text Channels</div>
        {CLIENT_CHANNELS.filter((c) => c.kind !== 'world').map((c) => (
          <ChannelItem
            key={c.id}
            channel={c}
            active={c.id === activeChannel}
            unread={unread[c.id] ?? 0}
            onSelect={() => selectChannel(c.id)}
          />
        ))}

        <div className={styles.channelSectionTitle}>World</div>
        {CLIENT_CHANNELS.filter((c) => c.kind === 'world').map((c) => (
          <ChannelItem
            key={c.id}
            channel={c}
            active={c.id === activeChannel}
            unread={unread[c.id] ?? 0}
            onSelect={() => selectChannel(c.id)}
          />
        ))}
      </div>
    </section>
  );
}

interface ChannelItemProps {
  channel: Channel;
  active: boolean;
  unread: number;
  onSelect: () => void;
}

function ChannelItem({
  channel,
  active,
  unread,
  onSelect,
}: ChannelItemProps): JSX.Element {
  const placeholder = channel.kind === 'text-placeholder';
  return (
    <div
      className={clsx(
        styles.channelItem,
        active && styles.channelItemActive,
        placeholder && styles.channelItemPlaceholder,
      )}
      role="button"
      tabIndex={0}
      aria-pressed={active}
      aria-label={channel.label}
      title={channel.description}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <span className={styles.channelIcon} aria-hidden="true">
        {channel.kind === 'world' ? (
          <Globe size={16} />
        ) : placeholder ? (
          <Lock size={14} />
        ) : (
          <Hash size={16} />
        )}
      </span>
      <span className={styles.channelLabel}>{channel.label}</span>
      {unread > 0 ? (
        <span
          className={styles.channelUnreadDot}
          aria-label={`${unread} unread`}
        />
      ) : null}
    </div>
  );
}
